import type { WriteIdentity } from '@/core/api/http'
import { apiGetOneOrNull, apiPatch, apiPut } from '@/core/api/resources'
import type {
  CoachOption,
  CoachingCreate,
  CoachingGueltigkeit,
  CoachingVertragRef,
  CoachingVoucherRef,
} from '@/core/data/coachings'
import type { CoachingAzavPatch } from '../api/resources'

export async function getGueltigkeit(coachingId: string): Promise<CoachingGueltigkeit | null> {
  const row = await apiGetOneOrNull('/azav/coaching/{id}', { id: coachingId })
  return row ? { beginn: row.gueltigkeit_beginn, ende: row.gueltigkeit_ende } : null
}

export async function createCoaching(row: CoachingCreate): Promise<void> {
  await apiPut('/azav/coaching', {
    ...row,
    avgs_nummer: 0,
    inhalt_gruppen: '',
    inhalt_einzel: '',
    max_dauer: '',
    gueltigkeit_beginn: '1970-01-01',
    gueltigkeit_ende: '1970-01-01',
    zeitl_umf_kategorie: '',
    zeitl_umf_max_ue: 0,
  })
}

export async function patchCoaching(coachingId: string, patch: CoachingAzavPatch, identity?: WriteIdentity): Promise<void> {
  await apiPatch('/azav/coaching/{id}', { id: coachingId }, patch, identity)
}

export async function getCoachingVertragRef(coachingId: string): Promise<CoachingVertragRef | null> {
  const row = await apiGetOneOrNull('/azav/coaching/{id}', { id: coachingId }, {
    include: ['agentur', 'massnahme'],
  })
  if (!row) return null
  const massnahme = row.massnahme
    ? await apiGetOneOrNull('/azav/massnahme/{id}', { id: row.massnahme.id })
    : null
  return {
    avgs_nummer: row.avgs_nummer,
    zeitl_umf_max_ue: row.zeitl_umf_max_ue,
    agentur: row.agentur
      ? { behoerde: row.agentur.behoerde ?? '', standort: row.agentur.standort ?? '' }
      : null,
    massnahme: massnahme
      ? {
          nummer: massnahme.nummer,
          bezeichnung: massnahme.bezeichnung,
          ue_anzahl: massnahme.ue_anzahl,
          kosten: massnahme.kosten,
          abschluss: massnahme.abschluss,
        }
      : null,
  }
}

export async function getCoachingVoucherRef(coachingId: string): Promise<CoachingVoucherRef | null> {
  const row = await apiGetOneOrNull('/azav/coaching/{id}', { id: coachingId }, {
    include: ['massnahme'],
  })
  if (!row) return null
  return {
    inhalt_gruppen: row.inhalt_gruppen,
    inhalt_einzel: row.inhalt_einzel,
    zeitl_umf_kategorie: row.zeitl_umf_kategorie,
    zeitl_umf_max_ue: row.zeitl_umf_max_ue,
    gueltigkeit_beginn: row.gueltigkeit_beginn,
    gueltigkeit_ende: row.gueltigkeit_ende,
    max_dauer: row.max_dauer,
    massnahme: row.massnahme ? { nummer: row.massnahme.nummer } : null,
  }
}

export async function getAssignedCoaches(coachingId: string): Promise<CoachOption[]> {
  const row = await apiGetOneOrNull('/azav/coaching/{id}', { id: coachingId }, { include: ['members'] })
  if (!row) return []
  const second = row.second_coach_id
    ? await apiGetOneOrNull('/members/{id}', { id: row.second_coach_id })
    : null
  return [row.members, second].flatMap((member) => member ? [{
    id: member.id,
    name: [member.vorname, member.nachname].filter(Boolean).join(' ').trim(),
  }] : [])
}

export type TbEinreichung = 'digital' | 'original'

export async function getTbEinreichung(coachingId: string): Promise<{
  einreichung: TbEinreichung
  agentur: { behoerde: string; standort: string } | null
} | null> {
  const row = await apiGetOneOrNull('/azav/coaching/{id}', { id: coachingId }, { include: ['agentur'] })
  if (!row) return null
  return {
    einreichung: row.tb_einreichung === 'original' ? 'original' : 'digital',
    agentur: row.agentur ? { behoerde: row.agentur.behoerde ?? '', standort: row.agentur.standort ?? '' } : null,
  }
}

export async function setTbEinreichung(coachingId: string, einreichung: TbEinreichung, identity?: WriteIdentity): Promise<void> {
  await patchCoaching(coachingId, { tb_einreichung: einreichung }, identity)
}
